import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import type { UserQuizBuildInfo } from '@/api/gen/_model/userQuizBuildInfo';
import { type SsrTranslator } from '@/utils/ssr';
import { type BuildSchemaInputType } from '@/views/build/form/schema';
import { type PublisherInfo } from '@/views/build/type';

export const getPublisherType = (
  publishers: PublisherInfo[],
  originSetData?: QuizSetDetailsRes,
): 'NONE' | 'REGULAR' | 'CUSTOM' => {
  if (!originSetData?.categoryTreeData?.publisher) {
    return 'NONE';
  }

  if (
    !publishers.some(d => d.value === originSetData.categoryTreeData?.publisher)
  ) {
    return 'CUSTOM';
  }

  return 'REGULAR';
};

const getTreeId = (categoryTreeId = '') => {
  const schoolTypeId = categoryTreeId.slice(0, 3);
  const gradeId = categoryTreeId.slice(0, 5);
  const subject = categoryTreeId.slice(0, 7);

  const subSubject =
    categoryTreeId.length > 7 ? categoryTreeId.slice(0, 9) : undefined;

  return {
    schoolTypeId,
    gradeId,
    subject,
    subSubject,
    categoryTreeId,
  };
};

const getPublisherInfo = (publisherType: string, publisherValue: string) => {
  return {
    publisher:
      publisherType === 'CUSTOM'
        ? 'OTHER'
        : publisherType === 'NONE'
        ? ''
        : publisherValue,
    customPublisher: publisherType === 'CUSTOM' ? publisherValue : '',
  };
};

export const findSchoolInfoDefaultFormValue = (props: {
  userSavedData: UserQuizBuildInfo['lastQuizSetCreationInfo'];
  publishers: PublisherInfo[];
  originSetData?: QuizSetDetailsRes;
  isInitialize: boolean;
}): Partial<BuildSchemaInputType> => {
  const { userSavedData, originSetData, publishers, isInitialize } = props;
  const initValue: Partial<BuildSchemaInputType> = {
    schoolTypeId: '',
    semesterId: undefined,
    gradeId: undefined,
    subject: '',
    subSubject: '',
    publisher: '',
    customPublisher: '',
  };
  //

  if (isInitialize) {
    return initValue;
  }
  const publisherType = getPublisherType(publishers, originSetData);

  const { publisher, customPublisher } = getPublisherInfo(
    publisherType,
    originSetData?.categoryTreeData?.publisher ?? '',
  );

  // 확인 하는 순서: 업데이트 -> 기존 유저 정보, 빈값
  if (originSetData?.categoryTreeData) {
    const { schoolTypeId, gradeId, subject, subSubject } = getTreeId(
      originSetData.categoryTreeData.categoryTreeId,
    );

    return {
      schoolTypeId,
      gradeId,
      subject,
      semesterId: originSetData.categoryTreeData.schoolSemester ?? undefined,
      subSubject,
      publisher,
      customPublisher,
    };
  }

  if (userSavedData) {
    const { schoolTypeId, gradeId, subject, subSubject } = getTreeId(
      userSavedData.categoryTreeId,
    );

    return {
      schoolTypeId,
      semesterId: userSavedData.schoolSemester ?? undefined,
      gradeId,
      subject,
      subSubject,
      publisher,
      customPublisher: '',
    };
  }

  //TODO: userProfileInfo 폴백 로직 추가

  return initValue;
};

export const ALL_GRADES_NUMBER = 99;

export const isAllGrades = (
  gradeNumber: number | string | null | undefined,
): boolean => Number(gradeNumber) === ALL_GRADES_NUMBER;

export const getGradeNumber = (gradeId?: number | string): number => {
  if (!gradeId) return 0;
  return Number(gradeId) % 100;
};

export function getGradeLabel(t: SsrTranslator, grade?: number): string {
  if (!grade) return 'all';
  if (isAllGrades(grade)) {
    return t({ key: 'school.info.allGrades', defaultValue: '전학년' });
  }
  return t({
    key: 'school.info.nGrade',
    replacements: { n: grade.toString() },
  });
}
